import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trophy, AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { api, session } from "@/lib/api";

/**
 * iter87.182 — Two-step magic-link consumption.
 *
 * Prior behavior auto-fired `consumeRecovery(token)` inside a mount effect.
 * That broke in two real-world scenarios:
 *
 *   1. Enterprise email scanners (Microsoft Safe Links / ATP, Proofpoint,
 *      Cisco IronPort, Barracuda) issue HEAD/GET requests against every
 *      URL in inbound mail, and many of them run headless JS to detect
 *      redirects. Their pre-fetch would consume the single-use token
 *      before the recipient could tap the link. Recipient then landed on
 *      the "This recovery link was already used" error card.
 *   2. React 18 StrictMode double-invokes the mount effect in dev builds.
 *      The prior `useRef` guard did NOT survive StrictMode's simulated
 *      unmount/remount cycle (a fresh ref is created on remount), so the
 *      backend saw two POSTs — first consumed, second raced back with
 *      "already used" and overwrote the success state.
 *
 * Fix: require an explicit user click to consume the token. Landing on
 * `/recover/{token}` renders a big "Sign in →" button and no network
 * call. Scanners crawl the HTML shell (no token spend), user taps the
 * button (single spend), success + redirect. Matches the industry
 * pattern used by Slack / Notion / Superhuman for magic links.
 *
 * Also fixes the misleading "Request a new link" error-state button
 * (which navigated to `/` — the landing page — but did not actually
 * request a new link). Copy is now honest ("Back to home") and pairs
 * with a helper line telling the user exactly how to get a fresh link
 * (ask the rivalry admin — the only path currently wired for a
 * mid-session recipient without invite+email in hand).
 */
export default function Recover() {
    const { token } = useParams();
    const navigate = useNavigate();
    // idle → user hasn't tapped Sign in yet (default landing state).
    // consuming → POST in flight.
    // success → session saved, redirect scheduled.
    // error → backend returned 4xx (invalid / expired / used).
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");
    const [leagueName, setLeagueName] = useState("");
    // Guard against a rapid double-click racing two POSTs (would spend
    // the token on the first + error on the second before setState
    // settles). Refs beat state here because the click handler needs to
    // reject re-entry synchronously.
    const consumingRef = useRef(false);

    const signIn = async () => {
        if (consumingRef.current || status === "consuming" || status === "success") return;
        consumingRef.current = true;
        setStatus("consuming");
        try {
            const data = await api.consumeRecovery(token);
            session.save(data);
            setLeagueName(data.league?.name || "");
            setStatus("success");
            // Brief success beat so the user reads "You're in." before
            // the router yanks the page.
            setTimeout(() => navigate(`/league/${data.league.id}`), 900);
        } catch (err) {
            setError(err.response?.data?.detail || "Recovery link is invalid or expired");
            setStatus("error");
            // Reset the guard so a user can retry if the failure was
            // transient (e.g. flaky mobile network) — the backend rejects
            // already-consumed tokens with a clear 4xx, so retry is
            // safe.
            consumingRef.current = false;
        }
    };

    // No-op effect kept only to preserve the token dep hook shape for
    // any future param-change behaviour. We deliberately do NOT call
    // signIn() from here — see block comment above.
    useEffect(() => {
        // token change resets state so a stale error doesn't stick
        // around if the URL is edited mid-session.
        setStatus("idle");
        setError("");
        consumingRef.current = false;
    }, [token]);

    return (
        <div className="min-h-screen flex items-center justify-center px-6">
            <div className="brutal-card p-8 max-w-md w-full text-center space-y-4" data-testid="recover-card">
                <div className="w-12 h-12 mx-auto bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-black" strokeWidth={2.5} />
                </div>

                {status === "idle" && (
                    <>
                        <h1 className="font-display font-black text-2xl">Sign in to your rivalry</h1>
                        <p className="text-zinc-400 text-sm">Tap the button below to open your magic link. It expires in 30 minutes and can be used once.</p>
                        <button
                            data-testid="recover-sign-in-btn"
                            onClick={signIn}
                            className="brutal-btn-primary w-full flex items-center justify-center gap-2"
                        >
                            Sign in <ArrowRight className="w-4 h-4" />
                        </button>
                    </>
                )}

                {status === "consuming" && (
                    <>
                        <Loader2 className="w-6 h-6 mx-auto text-cyber animate-spin" />
                        <h1 className="font-display font-black text-2xl">Verifying your link…</h1>
                        <p className="text-zinc-400 text-sm">Hang tight while we get you back in.</p>
                    </>
                )}

                {status === "success" && (
                    <>
                        <h1 className="font-display font-black text-2xl text-cyber">You're in.</h1>
                        <p className="text-zinc-400 text-sm">Redirecting to {leagueName || "your rivalry"}…</p>
                    </>
                )}

                {status === "error" && (
                    <>
                        <AlertCircle className="w-6 h-6 mx-auto text-red-500" />
                        <h1 className="font-display font-black text-2xl">Couldn't sign you in</h1>
                        <p className="text-zinc-400 text-sm" data-testid="recover-error-text">{error}</p>
                        <p className="text-zinc-500 text-xs leading-relaxed" data-testid="recover-error-help">
                            Ask your rivalry admin to send you a fresh join link — magic links expire after 30 minutes and can only be tapped once.
                        </p>
                        <button
                            data-testid="recover-back-home-btn"
                            onClick={() => navigate("/")}
                            className="brutal-btn-primary w-full flex items-center justify-center gap-2"
                        >
                            Back to home <ArrowRight className="w-4 h-4" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
